import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AiFillFilePdf } from "react-icons/ai";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { addWorkshop, editWorkshop, getSingleWorkshop } from "../../redux/services/workshopSlice";
import { ROUTES } from "../../constant";
import DescriptionEditor from "../Blog/DescriptionEditor";
import { CgRemove } from "react-icons/cg";
import { BiPlusCircle } from "react-icons/bi";

const WorkshopForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const singleWorkshopData = useSelector((state) => state.workshop.singleWorkshopData);

  const queryParams = new URLSearchParams(location.search);
  const workshopId = queryParams.get("workshopId");

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [description, setDescription] = useState(singleWorkshopData?.description);
  const [metaDescription, setMetaDescription] = useState([""]);

  // ✅ Formik initial values
  const initialValues = {
    title: "",
    image: "",
    syllabus: "",
    language: "",
    moneyBack: "",
    priority: "",
    totalTest: "",
    totalLecture: null,
    description: "",
    pdf: "",
    totalAmount: "",
    discountAmount: "",
    metaTitle: "",
    metaHeading: "",
    metaDescription: [""],
  };

  // ✅ Yup Validation Schema
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    image: Yup.string().required("Image is required"),
    language: Yup.string().required("Language is required"),
    syllabus: Yup.string().required("Syllabus is required"),
    moneyBack: Yup.string().required("Moneyback is required"),
    priority: Yup.number().typeError("Priority must be a number").required("Priority is required"),
    totalTest: Yup.number().typeError("Test number must be a number").required("Test number is required"),
    totalLecture: Yup.number().typeError("Test number must be a number").required("Lecture number is required"),
    description: Yup.string().required("Description is required"),
    totalAmount: Yup.number().typeError("Enter a valid amount").required("Total amount is required"),
    discountAmount: Yup.number().typeError("Enter a valid amount").required("Discount amount is required"),
  });

  // ✅ onSubmit handler
  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      let response;
      if (workshopId) {
        response = await dispatch(editWorkshop({ workshopRegisterId: workshopId, ...values }));
      } else {
        response = await dispatch(addWorkshop(values));
      }

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, { autoClose: 2000, pauseOnHover: false });
        setDescription("");
        resetForm();
        navigate(ROUTES.WORKSHOPS.BASE);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setValues } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  // ✅ Fetch Single Record for Edit
  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleWorkshop(workshopId));
    } catch (error) {}
  }, [dispatch, workshopId]);

  useEffect(() => {
    if (workshopId) getSingleRecord();
  }, [workshopId, getSingleRecord]);

  useEffect(() => {
    if (workshopId && singleWorkshopData) {
      setValues({
        title: singleWorkshopData?.title ?? "",
        image: singleWorkshopData?.image ?? "",
        syllabus: singleWorkshopData?.syllabus ?? "",
        language: singleWorkshopData?.language ?? "",
        moneyBack: singleWorkshopData?.moneyBack ?? false,
        priority: singleWorkshopData?.priority ?? "",
        totalTest: singleWorkshopData?.totalTest ?? "",
        totalLecture: singleWorkshopData?.totalLecture ?? "",
        description: singleWorkshopData?.description ?? "",
        pdf: singleWorkshopData?.pdf ?? "",
        totalAmount: singleWorkshopData?.totalAmount ?? "",
        discountAmount: singleWorkshopData?.discountAmount ?? "",
        metaTitle: singleWorkshopData?.metaTitle ?? "",
        metaHeading: singleWorkshopData?.metaHeading ?? "",
      });
      setDescription(singleWorkshopData.description || "");
      setMetaDescription(singleWorkshopData.metaDescription?.length ? singleWorkshopData.metaDescription : [""]);
      setPreview(singleWorkshopData.image);
      if (singleWorkshopData.pdf) setPdfPreview(singleWorkshopData.pdf);
    }
  }, [workshopId, singleWorkshopData, setValues]);

  // ✅ File Upload Handler
  const handleFileUpload = async (file, imageName) => {
    const formData = new FormData();
    formData.append(imageName, file);
    try {
      setLoading(true);

      const response = await dispatch(fileUpload(formData));
      if (response?.payload) {
        setFieldValue(imageName, response.payload);
        setLoading(false);
      }
    } catch (error) {
      alert("Error uploading file. Please try again.");
    }
  };

  // ✅ Image + PDF handlers
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPdfPreview(fileURL);
      handleFileUpload(file, "pdf");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      handleFileUpload(file, "image");
    }
  };

  const handleMetaDescriptionChange = (index, value) => {
    const updated = [...metaDescription];
    updated[index] = value;
    setMetaDescription(updated);
    setFieldValue("metaDescription", updated); // sync with Formik
  };

  const addMetaDescription = () => {
    const updated = [...metaDescription, ""];
    setMetaDescription(updated);
    setFieldValue("metaDescription", updated);
  };

  const removeMetaDescription = (index) => {
    const updated = metaDescription.filter((_, i) => i !== index);
    setMetaDescription(updated);
    setFieldValue("metaDescription", updated);
  };

  useEffect(() => {
    if (workshopId && singleWorkshopData?.description) {
      setDescription(singleWorkshopData?.description);
    } else {
      setDescription("");
    }
  }, [singleWorkshopData]);

  return (
    <div className="bg-background py-6">
      <div className="px-4 py-6 bg-card shadow rounded-3xl sm:p-10">
        <p className="md:text-3xl text-xl text-center font-semibold uppercase text-primary">{action} Workshop</p>
        <form className="mt-6" onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-foreground">Title</label>
              <input type="text" name="title" value={values.title} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.title && errors.title && <div className="text-red-500 text-sm">{errors.title}</div>}
            </div>

            {/* Language */}
            <div>
              <label className="text-sm font-medium text-foreground">Language</label>
              <input type="text" name="language" value={values.language} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.language && errors.language && <div className="text-red-500 text-sm">{errors.language}</div>}
            </div>

            {/* Syllabus */}
            <div>
              <label className="text-sm font-medium text-foreground">Subject Level</label>
              <input type="text" name="syllabus" className="w-full px-4 py-2 border border-border rounded-xl" value={values.syllabus} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Basic / Intermediate / Revision" />
            </div>
            {/* <div>
              <label className="text-sm font-medium text-foreground">Syllabus</label>
              <select name="syllabus" value={values.syllabus} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl">
                <option value="">Select Syllabus</option>
                <option value="subjectLevel">Subject Level</option>
                <option value="fullSyllabus">Full Syllabus</option>
              </select>
              {touched.syllabus && errors.syllabus && <div className="text-red-500 text-sm">{errors.syllabus}</div>}
            </div> */}

            {/* Money Back */}
            <div>
              <label className="text-sm font-medium text-foreground">Money Back</label>
              <input type="text" name="moneyBack" value={values.moneyBack} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.moneyBack && errors.moneyBack && <div className="text-red-500 text-sm">{errors.moneyBack}</div>}
            </div>
            {/* <div>
              <label className="text-sm font-medium text-foreground">Money Back</label>
              <select name="moneyBack" value={values.moneyBack} onChange={(e) => setFieldValue("moneyBack", e.target.value === "true")} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl">
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div> */}

            {/* Priority */}
            <div>
              <label className="text-sm font-medium text-foreground">Priority</label>
              <input type="number" name="priority" value={values.priority} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.priority && errors.priority && <div className="text-red-500 text-sm">{errors.priority}</div>}
            </div>

            {/* Test Number */}
            <div>
              <label className="text-sm font-medium text-foreground">Test Number</label>
              <input type="number" name="totalTest" value={values.totalTest} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.totalTest && errors.totalTest && <div className="text-red-500 text-sm">{errors.totalTest}</div>}
            </div>

            {/* Test Number */}
            <div>
              <label className="text-sm font-medium text-foreground">Lecture Number</label>
              <input type="number" name="totalLecture" value={values.totalLecture} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.totalLecture && errors.totalLecture && <div className="text-red-500 text-sm">{errors.totalLecture}</div>}
            </div>

            {/* Description */}
            {/* <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" rows="4" />
              {touched.description && errors.description && <div className="text-red-500 text-sm">{errors.description}</div>}
            </div> */}

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-foreground">Description</label>

              <DescriptionEditor
                value={description}
                // onChange={setDescription}
                onChange={(content) => {
                  setDescription(content);
                  setValues({ ...values, description: content }); // ✅ update Formik too
                }}
                readOnly={false}
              />
              {touched.description && errors.description && <div className="text-red-500 text-sm">{errors.description}</div>}
            </div>

            {/* Total Amount */}
            <div>
              <label className="text-sm font-medium text-foreground">Total Amount</label>
              <input type="number" name="totalAmount" value={values.totalAmount} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.totalAmount && errors.totalAmount && <div className="text-red-500 text-sm">{errors.totalAmount}</div>}
            </div>

            {/* Discount Amount */}
            <div>
              <label className="text-sm font-medium text-foreground">Discount Amount</label>
              <input type="number" name="discountAmount" value={values.discountAmount} onChange={handleChange} onBlur={handleBlur} className="w-full px-4 py-2 border border-border rounded-xl" />
              {touched.discountAmount && errors.discountAmount && <div className="text-red-500 text-sm">{errors.discountAmount}</div>}
            </div>

            {/* Image Upload */}
            <div>
              <label className="text-sm font-medium text-foreground">Image</label>
              <input type="file" name="image" onChange={handleImageChange} className="w-full px-4 py-2 border border-border rounded-xl" />
              {preview && <img src={preview} alt="Preview" className="w-20 h-20 mt-2 rounded-xl object-cover border" />}
              {touched.image && errors.image && <div className="text-red-500 text-sm">{errors.image}</div>}
            </div>

            {/* PDF Upload */}
            <div>
              <label className="text-sm font-medium text-foreground">PDF</label>
              <input type="file" name="pdf" accept=".pdf" onChange={handleFileChange} className="w-full px-4 py-2 border border-border rounded-xl" />
              {pdfPreview && (
                <Link to={pdfPreview} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary mt-3">
                  <AiFillFilePdf size={35} className="mr-2" /> View PDF
                </Link>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Meta Title</label>
              <input type="text" name="metaTitle" className="w-full px-4 py-2 border border-border rounded-xl" value={values.metaTitle} onChange={handleChange} onBlur={handleBlur} placeholder="Workshop Meta Title" />
              {touched.metaTitle && errors.metaTitle && <div className="text-red-500 text-sm">{errors.metaTitle}</div>}
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Meta Heading</label>
              <input type="text" name="metaHeading" className="w-full px-4 py-2 border border-border rounded-xl" value={values.metaHeading} onChange={handleChange} onBlur={handleBlur} placeholder="Workshop Meta Heading" />
              {touched.metaHeading && errors.metaHeading && <div className="text-red-500 text-sm">{errors.metaHeading}</div>}
            </div>
            <div className="md:col-span-2">
              {metaDescription.map((item, index) => (
                <div key={index} className="flex gap-2 mt-2">
                    <div className="w-full">
                      <label className="text-sm font-medium text-foreground">Meta Description</label>
                      <input type="text" className="w-full px-4 py-2 border border-border rounded-xl" value={item} onChange={(e) => handleMetaDescriptionChange(index, e.target.value)} placeholder="Workshop Meta Description" />
                    </div>
                  {metaDescription.length > 1 && (
                    <button type="button" onClick={() => removeMetaDescription(index)} className="md:h-10 md:w-10 w-8 mt-6 flex justify-center items-center text-white bg-red-500 rounded-xl hover:bg-red-600">
                      <CgRemove className="text-sm sm:text-xl" />
                    </button>
                  )}

                  {index === metaDescription.length - 1 && (
                    <button type="button" onClick={addMetaDescription} className="md:h-10 md:w-10 w-8 mt-6 flex justify-center items-center text-white bg-primary rounded-xl">
                      <BiPlusCircle className="text-sm sm:text-2xl" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <button type="submit" disabled={loading} className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7">
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkshopForm;
